import { useQuery } from '@tanstack/react-query';
import type { DispatchProgress } from '@/features/sales-ops/hooks/useSalesOpsApi';
import { api } from '@/shared/api/axios';

/** A single line on a Quotation/Proforma/Invoice — all three share this shape
 *  (SalesOrderModel, db-sales.js). */
export interface SalesDocLine {
  productId?: { _id: string; name: string; sku?: string } | string | null;
  description?: string;
  quantity: number;
  unitPrice: number;
  discount?: number;
  discountType?: 'fixed' | 'percent';
  cgstRate?: number;
  sgstRate?: number;
  igstRate?: number;
  lineTotal?: number;
  hsnSac?: string;
  unit?: string;
  packSize?: string;
  qtyPerPack?: number;
}

/** Quotation, Proforma and direct-sale Invoice are the SAME model
 *  (SalesOrderModel), discriminated by documentType — one detail page
 *  serves all three, the same way the list pages already share their shape. */
export interface SalesDoc {
  dispatchProgress?: DispatchProgress;
  _id: string;
  orderNumber?: string;
  documentType: 'quotation' | 'proforma' | 'direct_sale' | 'sales_order' | 'delivery_challan';
  status: string;
  /** 1 for the original, 2 for its first revision, and so on (db-sales.js). */
  revisionNumber: number;
  /** The ROOT document of this revision chain, when this is not the original. */
  revisionOfId?: string | null;
  /** When this document was last actually delivered to the client, and how.
   *  Null means it has never been sent from this system — which is allowed,
   *  but is worth saying out loud before recording a customer's acceptance. */
  lastSentAt?: string | null;
  lastSentVia?: string[];
  date: string;
  validUntil?: string | null;
  customerId?: { _id: string; name: string; email?: string; phone?: string } | string | null;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerGstin?: string;
  /** Customer purchase order captured on the document (db-sales.js:193-194).
   *  The detail page reads these; the interface never declared them. */
  customerPoNumber?: string;
  customerPoDate?: string | null;
  customerPoAttachments?: { fileUrl: string; originalName?: string; uploadedAt?: string }[];
  billingAddress?: Record<string, any> | null;
  shippingAddress?: Record<string, any> | null;
  shippingCharge?: number;
  freightCharges?: number;
  crmContactId?: string | null;
  crmDealId?: string | null;
  crmLeadId?: string | null;
  lines: SalesDocLine[];
  subTotal?: number;
  taxTotal?: number;
  grandTotal?: number;
  notes?: string;
  lostReasonId?: { _id: string; name: string } | string | null;
  lostReasonNote?: string;
  createdBy?: { _id?: string; display_name?: string; username?: string } | string | null;
  /** The salesperson the document is credited to. */
  originatingRepId?: string | { _id?: string } | null;
  /** Payment received against the document, and what is still due (detail view). */
  amountPaid?: number;
  balanceDue?: number;
  createdAt?: string;
  confirmedAt?: string | null;
  // Conversion provenance (db-sales.js) — the document this one was raised
  // from (backward link) and what it has since become (forward links). Not
  // shown anywhere in the UI until now; admin/superadmin had no way to trace
  // the quotation -> proforma -> invoice chain outside the printed PDF.
  convertedFromId?: string | null;
  convertedFromType?: string;
  convertedFromNumber?: string;
  linkedDocuments?: { docType: string; docId: string; docNumber: string; createdAt: string }[];
  accountsInvoiceId?: { _id: string; invoiceNumber?: string } | string | null;
  /** Set only when accountsInvoiceId is empty — the real Tax Invoice flow
   *  (Quotation -> Invoice Request -> generate-invoice) never writes that
   *  field; the backend resolves this via a reverse lookup instead. */
  formalInvoiceNumber?: string;
}

export function useSalesDoc(id: string | null) {
  return useQuery({
    queryKey: ['sales-doc', 'detail', id],
    queryFn: async () => {
      const { data } = await api.get<SalesDoc>(`/api/sales-ops/orders/${id}`);
      return data;
    },
    enabled: !!id,
    refetchInterval: 15_000,
  });
}
